// src/components/Sidebar.jsx - WITH MEETING MODE TOGGLE
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Insights,
  Search,
  MenuBook,
  ChatBubble,
  AccountBox,
  Menu,
  Storage,
  GridOn,
  AttachMoney,
  Assessment,
  Description,
  CheckCircle
} from '@mui/icons-material';
import { ToggleRight, ToggleLeft } from 'lucide-react';
import { MeetingModeContext } from '../contexts/MeetingModeContext';
import { setMeetingMode } from '../utils/meetingMode';

// Meeting Mode Toggle Component
function MeetingModeToggle() {
  const { meetingMode } = useContext(MeetingModeContext);
  const onClick = useCallback(() => setMeetingMode(!meetingMode), [meetingMode]);
  const Icon = meetingMode ? ToggleRight : ToggleLeft;
  
  return (
    <button
      onClick={onClick}
      className={`mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all duration-200
        ${meetingMode 
          ? 'bg-orange-50 border-orange-200 text-orange-700 shadow-sm' 
          : 'bg-white border-slate-200 text-slate-600'
        }
        hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-300`}
      aria-pressed={meetingMode}
      aria-label="Toggle meeting mode"
      title="Toggle Meeting Mode - Simplifies UI for live presentations"
    >
      <Icon size={18} />
      <span className="truncate font-medium">Meeting Mode</span>
    </button>
  );
}

export default function Sidebar() {
  const [expanded, setExpanded] = useState(true);
  const [currentClientId, setCurrentClientId] = useState(() => {
    return localStorage.getItem('dva_current_client') || '200';
  });
  
  const location = useLocation();
  const navigate = useNavigate();

  // Simple client list - you can expand this later with API fetch
  const clients = [
    { id: '1', name: 'Baseline Analytics' },
    { id: '200', name: 'Regional Health System' },
    { id: '300', name: 'North Star Consortium' }
  ];

  const handleClientChange = (newClientId) => {
    // Update state
    setCurrentClientId(newClientId);
    
    // Update localStorage
    localStorage.setItem('dva_current_client', newClientId);

    // Update URL if we're on a page that uses client param
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('client')) {
      currentUrl.searchParams.set('client', newClientId);
      window.history.replaceState({}, '', currentUrl);
    }

    // Broadcast change event
    window.dispatchEvent(new CustomEvent('clientChanged', {
      detail: { clientId: newClientId }
    }));

    // Force page reload to refresh all data
    window.location.reload();
  };

  const menuItems = [
    { name: 'Home', icon: <Home />, link: `/home/${currentClientId}` },
    { name: 'Strategic Matrix', icon: <GridOn />, link: '/matrix' },
    { name: 'Use Case List', icon: <Search />, link: '/results' },
    { name: 'Strategic Alignment', icon: <Insights />, link: '/strategic-alignment' },
    { name: 'Playbook', icon: <MenuBook />, link: '/playbook' },
    { name: 'Action Board', icon: <CheckCircle />, link: `/action-board/${currentClientId}` },
    { name: 'Chat', icon: <ChatBubble />, link: '/chat' },
    { name: 'Client Profile', icon: <AccountBox />, link: `/client-profile/${currentClientId}` }
  ];

  const qaItems = [
    { name: 'Financial QA', icon: <AttachMoney />, link: '/financial-qa' },
    { name: 'Profile QA', icon: <Assessment />, link: '/profile-qa' },
    { name: 'Sections QA', icon: <Description />, link: '/sections-qa' },
    { name: 'Raw Data QA', icon: <Storage />, link: '/qa/selector' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#1f2937',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      width: expanded ? '260px' : '64px',
      transition: 'width 0.3s',
      position: 'relative'
    }}>
      {/* Header with Menu Toggle */}
      <div style={{
        padding: expanded ? '16px' : '16px 0',
        borderBottom: '1px solid #374151',
        backgroundColor: '#111827',
        display: 'flex',
        alignItems: 'center',
        justifyContent: expanded ? 'space-between' : 'center'
      }}>
        {expanded && (
          <div style={{ fontSize: '16px', fontWeight: '600' }}>
            DVA Dashboard
          </div>
        )}
        <button
          style={{
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            color: 'white',
            cursor: 'pointer',
            border: 'none',
            borderRadius: '4px',
            transition: 'background-color 0.2s'
          }}
          onClick={() => setExpanded(!expanded)}
          title={expanded ? 'Collapse Menu' : 'Expand Menu'}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <Menu />
        </button>
      </div>

      {/* Client Selector - SIMPLIFIED */}
      <div style={{
        padding: expanded ? '12px 16px' : '12px 8px',
        borderBottom: '1px solid #374151',
        backgroundColor: '#0f172a'
      }}>
        {expanded ? (
          <>
            <div style={{
              fontSize: '10px',
              color: '#9CA3AF',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '6px',
              fontWeight: '600'
            }}>
              Active Client
            </div>
            <select
              value={currentClientId}
              onChange={(e) => handleClientChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#1f2937',
                color: 'white',
                border: '1px solid #374151',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#60a5fa',
            textAlign: 'center'
          }}>
            C{currentClientId}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 8px',
        gap: '4px',
        overflowY: 'auto'
      }}>
        {/* Main Menu Items */}
        {menuItems.map(({ name, icon, link }) => (
          <NavLink
            key={name}
            to={link}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              padding: expanded ? '10px 12px' : '10px',
              borderRadius: '6px',
              transition: 'all 0.2s',
              textDecoration: 'none',
              color: isActive ? '#60a5fa' : '#d1d5db',
              backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              fontWeight: isActive ? '500' : '400',
              fontSize: '14px',
              justifyContent: expanded ? 'flex-start' : 'center'
            })}
            title={!expanded ? name : ''}
            onMouseEnter={(e) => {
              if (!e.currentTarget.classList.contains('active')) {
                e.currentTarget.style.backgroundColor = '#374151';
              }
            }}
            onMouseLeave={(e) => {
              if (!e.currentTarget.classList.contains('active')) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <span style={{ fontSize: '20px', display: 'flex' }}>{icon}</span>
            {expanded && <span style={{ marginLeft: '12px' }}>{name}</span>}
          </NavLink>
        ))}
        
        {/* QA Tools Section */}
        <div style={{
          marginTop: 'auto',
          paddingTop: '16px',
          borderTop: '1px solid #374151'
        }}>
          {expanded && (
            <div style={{
              padding: '0 12px 8px',
              fontSize: '10px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: '600'
            }}>
              QA Tools
            </div>
          )}
          {qaItems.map(({ name, icon, link }) => (
            <NavLink
              key={name}
              to={link}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                padding: expanded ? '8px 12px' : '8px',
                borderRadius: '6px',
                transition: 'all 0.2s',
                textDecoration: 'none',
                color: isActive ? '#fbbf24' : '#9ca3af',
                backgroundColor: isActive ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
                fontWeight: isActive ? '500' : '400',
                fontSize: '13px',
                justifyContent: expanded ? 'flex-start' : 'center'
              })}
              title={!expanded ? name : ''}
              onMouseEnter={(e) => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.backgroundColor = '#374151';
                  e.currentTarget.style.color = '#d1d5db';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#9ca3af';
                }
              }}
            >
              <span style={{ fontSize: '18px', display: 'flex' }}>{icon}</span>
              {expanded && <span style={{ marginLeft: '10px' }}>{name}</span>}
            </NavLink>
          ))}
          
          {/* Meeting Mode Toggle - Added at bottom */}
          {expanded && (
            <div style={{ marginTop: '12px', padding: '0 4px' }}>
              <MeetingModeToggle />
            </div>
          )}
        </div>
      </nav>
    </div>
  );
}